import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { apiRequest } from '../utils/apiRequest';
import { API_URLS } from '../config';
import Select from 'react-select';
import FilterPanel, { FilterField, filterSelectStyles } from '../Components/FilterPanel';
import './Calendar.css';


const WEEKDAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const currentMonthKey = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
};

const monthTitle = (ym) => {
  const [y, m] = ym.split('-').map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
};

// Weeks of YYYY-MM-DD strings (or null for the blanks before/after the month)
// for the given YYYY-MM, aligned Sunday-first.
const buildMonthGrid = (ym) => {
  const [y, m] = ym.split('-').map(Number);
  const daysInMonth = new Date(y, m, 0).getDate();
  const startWeekday = new Date(y, m - 1, 1).getDay();

  const cells = Array(startWeekday).fill(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(`${ym}-${String(d).padStart(2, '0')}`);
  while (cells.length % 7 !== 0) cells.push(null);

  const weeks = [];
  for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));
  return weeks;
};

const STATUS_CLASS = {
  WON: 'cal-entry-won',
  LOST: 'cal-entry-lost',
  REJECT: 'cal-entry-lost',
  INPROCESS: 'cal-entry-inprocess',
  NEW: 'cal-entry-new',
};

const MAX_VISIBLE_ENTRIES = 3;

const CalendarEntry = ({ entry, inspectorId }) => (
  <a
    href={`/enquiry/manday/${entry.enquiryId}`}
    target="_blank"
    rel="noopener noreferrer"
    className={`cal-entry ${STATUS_CLASS[entry.status] || 'cal-entry-new'}`}
    title={`${entry.jobNumber} — ${entry.inspectorName} (${entry.status})`}
  >
    <span className="cal-entry-job">{entry.jobNumber}</span>
    {!inspectorId && <span className="cal-entry-inspector">{entry.inspectorName}</span>}
  </a>
);

const Calendar = () => {
  const { user } = useAuth();
  const isGlobalSuperAdmin = user?.role === 'global_superadmin';

  const [month, setMonth] = useState(currentMonthKey());
  const [inspectors, setInspectors] = useState([]);
  const [inspectorId, setInspectorId] = useState('');
  const [coordinators, setCoordinators] = useState([]);
  const [coordinatorId, setCoordinatorId] = useState('');
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState('');
  const statusFilter = 'WON';
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [moreDate, setMoreDate] = useState(null);

  useEffect(() => {
    const loadInspectors = async () => {
      const token = localStorage.getItem('token');
      const res = await apiRequest(`${API_URLS.INSPECTORS}?all=true`, { method: 'GET' }, token);
      if (res?.ok) setInspectors(res.data?.rows || []);
    };
    loadInspectors();
  }, []);

  useEffect(() => {
    if (!isGlobalSuperAdmin) return;
    const loadCompanies = async () => {
      const token = localStorage.getItem('token');
      const res = await apiRequest(API_URLS.COMPANIES, { method: 'GET' }, token);
      if (res?.ok) setCompanies(Array.isArray(res.data) ? res.data : (res.data?.rows || []));
    };
    loadCompanies();
  }, [isGlobalSuperAdmin]);

  // Re-loads whenever companyId changes too, so the dropdown's own options stay
  // scoped to whichever company is currently selected (admin only).
  useEffect(() => {
    const loadCoordinators = async () => {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (companyId) params.set('companyId', companyId);
      const res = await apiRequest(`${API_URLS.CALENDAR}/coordinators?${params.toString()}`, { method: 'GET' }, token);
      if (res?.ok) setCoordinators(res.data?.data?.coordinators || []);
    };
    loadCoordinators();
  }, [companyId]);

  const fetchEntries = useCallback(async () => {
    setLoading(true);
    const token = localStorage.getItem('token');
    const params = new URLSearchParams({ month });
    if (inspectorId) params.set('inspectorId', inspectorId);
    if (coordinatorId) params.set('coordinatorId', coordinatorId);
    if (companyId) params.set('companyId', companyId);
    if (statusFilter) params.set('status', statusFilter);
    const res = await apiRequest(`${API_URLS.CALENDAR}/inspections?${params.toString()}`, { method: 'GET' }, token);
    if (res?.ok) setEntries(res.data?.data?.entries || []);
    setLoading(false);
  }, [month, inspectorId, coordinatorId, companyId, statusFilter]);

  useEffect(() => { fetchEntries(); }, [fetchEntries]);
  useEffect(() => { setMoreDate(null); }, [month, inspectorId, coordinatorId, companyId, statusFilter]);

  const entriesByDate = useMemo(() => {
    const map = {};
    for (const e of entries) {
      (map[e.date] ||= []).push(e);
    }
    return map;
  }, [entries]);

  const weeks = useMemo(() => buildMonthGrid(month), [month]);
  const todayStr = useMemo(() => {
    const t = new Date();
    return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`;
  }, []);

  const shiftMonth = (delta) => {
    const [y, m] = month.split('-').map(Number);
    const next = new Date(y, m - 1 + delta, 1);
    setMonth(`${next.getFullYear()}-${String(next.getMonth() + 1).padStart(2, '0')}`);
  };

  return (
    <div className="cal-page">
      <div className="cal-header">
        <h2>Inspection Calendar</h2>
        <div className="cal-controls">
          <div className="cal-month-nav">
            <button type="button" onClick={() => shiftMonth(-1)} aria-label="Previous month">&#8249;</button>
            <span className="cal-month-title">{monthTitle(month)}</span>
            <button type="button" onClick={() => shiftMonth(1)} aria-label="Next month">&#8250;</button>
          </div>
          <FilterPanel
            values={{
              company: companyId,
              coordinator: coordinatorId,
              inspector: inspectorId,
            }}
            emptyValues={{ company: '', coordinator: '', inspector: '' }}
            onChange={(v) => {
              if (v.company !== companyId) { setCompanyId(v.company); setCoordinatorId(''); }
              else setCoordinatorId(v.coordinator);
              setInspectorId(v.inspector);
            }}
            title="Filters"
            width={isGlobalSuperAdmin ? 400 : 260}
            columns={isGlobalSuperAdmin ? 2 : 1}
          >
            {(filters, set) => (
              <>
                {isGlobalSuperAdmin && (
                  <FilterField label="Company">
                    <Select
                      options={[{ value: '', label: 'All companies' }, ...companies.map(c => ({ value: c.id || c._id, label: c.companyName }))]}
                      value={{ value: filters.company, label: companies.find(c => (c.id || c._id) === filters.company)?.companyName || 'All companies' }}
                      onChange={opt => set({ company: opt?.value || '', coordinator: '' })}
                      styles={filterSelectStyles}
                      isSearchable={false}
                      isClearable={false}
                    />
                  </FilterField>
                )}
                <FilterField label="Coordinator">
                  <Select
                    options={[{ value: '', label: 'All coordinators' }, ...coordinators.map(c => ({ value: c.id, label: c.name }))]}
                    value={{ value: filters.coordinator, label: coordinators.find(c => c.id === filters.coordinator)?.name || 'All coordinators' }}
                    onChange={opt => set({ coordinator: opt?.value || '' })}
                    styles={filterSelectStyles}
                    isSearchable={false}
                    isClearable={false}
                  />
                </FilterField>
                <FilterField label="Inspector">
                  <Select
                    options={[{ value: '', label: 'All inspectors' }, ...inspectors.map(i => ({ value: i.id, label: i.inspectorName }))]}
                    value={{ value: filters.inspector, label: inspectors.find(i => i.id === filters.inspector)?.inspectorName || 'All inspectors' }}
                    onChange={opt => set({ inspector: opt?.value || '' })}
                    styles={filterSelectStyles}
                    isSearchable
                    isClearable={false}
                  />
                </FilterField>
              </>
            )}
          </FilterPanel>
        </div>
      </div>

      {loading && <p className="cal-muted">Loading&hellip;</p>}

      {!loading && (
        <div className="cal-grid">
          {WEEKDAY_LABELS.map((w) => (
            <div key={w} className="cal-weekday">{w}</div>
          ))}
          {weeks.flat().map((dateStr, i) => {
            const dayEntries = dateStr ? (entriesByDate[dateStr] || []) : [];
            const visibleEntries = dayEntries.slice(0, MAX_VISIBLE_ENTRIES);
            const hiddenCount = dayEntries.length - visibleEntries.length;
            return (
              <div key={i} className={`cal-cell ${!dateStr ? 'cal-cell-blank' : ''} ${dateStr === todayStr ? 'cal-cell-today' : ''}`}>
                {dateStr && (
                  <>
                    <div className="cal-day-number">{Number(dateStr.slice(-2))}</div>
                    <div className="cal-day-entries">
                      {visibleEntries.map((entry, idx) => (
                        <CalendarEntry key={`${entry.enquiryId}-${entry.inspectorId}-${idx}`} entry={entry} inspectorId={inspectorId} />
                      ))}
                      {hiddenCount > 0 && (
                        <button
                          type="button"
                          className="cal-more-btn"
                          onClick={() => setMoreDate(dateStr)}
                        >
                          +{hiddenCount} more
                        </button>
                      )}
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}

      {moreDate && (
        <div className="cal-more-overlay" onClick={() => setMoreDate(null)}>
          <div className="cal-more-popover" onClick={(e) => e.stopPropagation()}>
            <div className="cal-more-header">
              <span>{new Date(`${moreDate}T00:00:00`).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
              <button type="button" className="cal-more-close" onClick={() => setMoreDate(null)} aria-label="Close">&times;</button>
            </div>
            <div className="cal-more-list">
              {(entriesByDate[moreDate] || []).map((entry, idx) => (
                <CalendarEntry key={`${entry.enquiryId}-${entry.inspectorId}-${idx}`} entry={entry} inspectorId={inspectorId} />
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Calendar;
